import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "./pages/login/Login";
import Billing from "./pages/billing/Billing";
import Navbar from "./components/navbar/Navbar";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminPage from "./pages/adminPanel/AdminPage";
import PrivateRoute from "./privateRoute/PrivateRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthCallback from "./pages/authCallback/AuthCallback";
import { AUTH_ROUTES } from "./utils/constants/apiRoute";
import { APP_ROUTES_PATHS } from "./utils/constants/appRoutes";
import OfflineBanner from "./components/banners/offlineBanner";
import InstallPwaButton from "./components/pwa/InstallPwaButton";
import PrinterRegister from "./pages/printerRegister/PrinterRegister";
import PrinterSettings from "./pages/printerSettings/PrinterSettings";
import RecentOrdersPage from "./pages/recentOrders/RecentOrdersPage";
import Reports from "./components/reports/Reports";
import DesktopUpdateStatus from "./renderer/components/DesktopUpdateStatus";
import { isElectron } from "./renderer/platform";
import { APP_CONFIG } from "./utils/runtimeConfig";

const env_roles = APP_CONFIG.roles;
/**
 * The main application component that sets up routing using React Router.
 *
 * @component
 * @returns {JSX.Element} The rendered App component.
 */
function AppContent() {
  const { session, loading } = useAuth();
  const isLoggedIn = Boolean(session?.token);
  const role = session?.role ?? null;

  return (
    <>
      <div className="App">
        {!loading && isLoggedIn && <Navbar />}
        {!isElectron() && <InstallPwaButton />}
        <OfflineBanner />
        <DesktopUpdateStatus />
        {loading ? (
          <main role="status" aria-live="polite">
            Loading Orderly…
          </main>
        ) : (
          <Routes>
            <Route
              path={APP_ROUTES_PATHS.HOME_PATH}
              element={
                isLoggedIn
                  ? role === env_roles.ADMIN
                    ? <Navigate to={APP_ROUTES_PATHS.ADMIN_PANEL_PATH} replace />
                    : <Navigate to={APP_ROUTES_PATHS.BILLING_PATH} replace />
                  : <LoginPage />
              }
            />

            <Route
              path={APP_ROUTES_PATHS.BILLING_PATH}
              element={
                <PrivateRoute
                  element={<Billing />}
                  requiredRole={env_roles.CASHIER}
                  fallbackPath={APP_ROUTES_PATHS.ADMIN_PANEL_PATH}
                />
              }
            />

            <Route
              path={APP_ROUTES_PATHS.RECENT_ORDERS_PATH}
              element={
                <PrivateRoute
                  element={<RecentOrdersPage />}
                  requiredRole={env_roles.CASHIER}
                  fallbackPath={APP_ROUTES_PATHS.ADMIN_PANEL_PATH}
                />
              }
            />

            <Route
              path={APP_ROUTES_PATHS.ADMIN_PANEL_PATH}
              element={
                <PrivateRoute
                  element={<AdminPage />}
                  requiredRole={env_roles.ADMIN}
                  fallbackPath={APP_ROUTES_PATHS.BILLING_PATH}
                />
              }
            />

            <Route
              path={APP_ROUTES_PATHS.PRINTER_SETTINGS_PATH}
              element={<PrivateRoute element={<PrinterSettings />} />}
            />

            <Route
              path={APP_ROUTES_PATHS.REPORTS_PATH}
              element={
                <PrivateRoute
                  element={<Reports />}
                  requiredRole={env_roles.CASHIER}
                  fallbackPath={APP_ROUTES_PATHS.BILLING_PATH}
                />
              }
            />

            <Route path={APP_ROUTES_PATHS.PRINTER_REGISTER_PATH} element={<PrinterRegister />} />

            <Route path={AUTH_ROUTES.OAUTH_CALLBACK} element={<AuthCallback />} />

            {/* Handle unknown routes */}
            <Route
              path="*"
              element={
                <PrivateRoute
                  element={<Navigate to={APP_ROUTES_PATHS.BILLING_PATH} replace />}
                  fallbackPath={APP_ROUTES_PATHS.BILLING_PATH}
                />
              }
            />
          </Routes>
        )}
      </div>
      <ToastContainer autoClose={900} />
    </>
  );
}

function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}
export default App;
